// src/stores/payrollAdditionStore.js
import { defineStore } from "pinia";
import { ref } from "vue";
import PayrollAdditionService from "@/services/payrollAddition.service";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const usePayrollAdditionStore = defineStore("payrollAddition", () => {
  // STATE
  const additions = ref([]);
  const loading = ref(false);
  const isLoading = ref(false);
  const error = ref(null);

  // ACTIONS
  const fetchAll = async () => {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_PAYROLL))) {
      additions.value = [];
      return;
    }

    loading.value = true;
    try {
      const res = await PayrollAdditionService.getAll();
      additions.value = res;
      error.value = null;
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to load additions";
    } finally {
      loading.value = false;
    }
  };

  const createAddition = async (payload) => {
    isLoading.value = true;
    try {
      const response = await PayrollAdditionService.create(payload);
      additions.value.push(response.data);
      toast("Payroll addition created successfully!");
      return response.data;
    } catch (err) {
      error.value =
        err.response?.data?.message || "Failed to create payroll addition";
      toast(error.value, "error");
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const updateAddition = async (id, payload) => {
    isLoading.value = true;
    try {
      const response = await PayrollAdditionService.update(id, payload);
      const index = additions.value.findIndex((a) => a.id === id);

      if (index !== -1) {
        additions.value[index] = response.data;
      }

      toast("Payroll addition updated successfully!");
      return response.data;
    } catch (err) {
      console.log(err);
      error.value =
        err.response?.data?.message || "Failed to update payroll addition";
      toast(error.value, "error");
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteAddition = async (id) => {
    isLoading.value = true;
    try {
      await PayrollAdditionService.delete(id);
      additions.value = additions.value.filter((a) => a.id !== id);
      toast("Payroll addition deleted successfully!");
    } catch (err) {
      error.value =
        err.response?.data?.message || "Failed to delete payroll addition";
      toast(error.value, "error");
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    additions,
    loading,
    isLoading,
    error,
    fetchAll,
    createAddition,
    updateAddition,
    deleteAddition,
  };
});
